import { useCallback, useEffect, useState } from "react";
import { Image, Modal, Pressable, StyleSheet, View } from "react-native";
import CustomText from "../../../common/general/CustomText/CustomText";
import { colors, radii, spacing, webTransitions } from "../../../../styles/design-system";

export interface ImageCardProps {
  status: "loading" | "completed" | "failed";
  url?: string | null;
  title?: string | null;
  description?: string | null;
}

function ShimmerPlaceholder() {
  return (
    <View style={styles.shimmerContainer}>
      <CustomText useTranslationText={false} text="Loading image..." size="s" style={styles.shimmerText} />
    </View>
  );
}

/** Fetch the image's natural aspect ratio so RN can size it correctly. */
function useImageAspectRatio(url: string | null | undefined) {
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  useEffect(() => {
    if (!url) return;
    Image.getSize(
      url,
      (w, h) => {
        if (h > 0) setAspectRatio(w / h);
      },
      () => {
        // Fallback to 4:3 if size fetch fails
        setAspectRatio(4 / 3);
      }
    );
  }, [url]);

  return aspectRatio;
}

export const ImageCard = ({ status, url, title, description }: ImageCardProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const aspectRatio = useImageAspectRatio(status === "completed" ? url : null);

  const caption = title || description;

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  if (status === "loading") {
    return (
      <View style={styles.wrapper}>
        <ShimmerPlaceholder />
      </View>
    );
  }

  if (status === "failed") {
    return (
      <View style={styles.wrapper}>
        <CustomText useTranslationText={false} text="Image failed to load." size="s" style={styles.errorText} />
      </View>
    );
  }

  if (status === "completed" && url && aspectRatio) {
    return (
      <>
        {/* Thumbnail — natural aspect, capped by maxHeight / maxWidth */}
        <Pressable
          onPress={openModal}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          style={[styles.wrapper, styles.imageWrapper, isHovered && styles.imageWrapperHover]}
          accessibilityRole="button"
          accessibilityLabel={caption ? `View full size: ${caption}` : "View full size image"}>
          <Image source={{ uri: url }} style={[styles.thumbnailImage, { aspectRatio }]} resizeMode="contain" />
          {isHovered && (
            <View style={styles.hoverOverlay}>
              <CustomText useTranslationText={false} text="Click to expand" size="xs" style={styles.hoverText} />
            </View>
          )}
        </Pressable>
        {caption ? <CustomText useTranslationText={false} text={caption} size="s" style={styles.captionText} /> : null}

        {/* Fullscreen modal with zoom */}
        <Modal
          visible={modalOpen}
          transparent
          animationType="fade"
          onRequestClose={closeModal}
          accessibilityViewIsModal
          aria-label={caption ?? "Image preview"}>
          <Pressable style={styles.modalBackdrop} onPress={closeModal}>
            {/* Close button */}
            <Pressable
              style={styles.modalCloseButton}
              onPress={closeModal}
              accessibilityRole="button"
              accessibilityLabel="Close">
              <CustomText useTranslationText={false} text="✕" size="l" style={styles.modalCloseText} />
            </Pressable>

            {/* Image — full resolution, native browser zoom (Ctrl+scroll / pinch) */}
            <View style={styles.modalImageContainer}>
              <Image source={{ uri: url }} style={[styles.modalImage, { aspectRatio }]} resizeMode="contain" />
            </View>

            {/* Caption */}
            {caption ? (
              <View style={styles.modalCaption}>
                <CustomText useTranslationText={false} text={caption} size="s" style={styles.modalCaptionText} />
              </View>
            ) : null}
          </Pressable>
        </Modal>
      </>
    );
  }

  // Still loading aspect ratio
  if (status === "completed" && url && !aspectRatio) {
    return (
      <View style={styles.wrapper}>
        <ShimmerPlaceholder />
      </View>
    );
  }

  // Fallback: status is "completed" but url is missing or aspect ratio never resolved
  return (
    <View style={styles.wrapper}>
      <CustomText useTranslationText={false} text="Image failed to load." size="s" style={styles.errorText} />
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    marginTop: spacing.sm,
    marginBottom: spacing.xs,
  },
  imageWrapper: {
    // No fixed width/height. maxWidth + dynamic aspectRatio let the image
    // take its natural shape within a reasonable bounding box.
    maxWidth: 720,
    maxHeight: 480,
    borderRadius: radii.xl,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.grey[50],
    position: "relative",
    ...webTransitions.fast,
  },
  imageWrapperHover: {
    borderColor: colors.grey[400],
  },
  thumbnailImage: {
    width: "100%",
    // aspectRatio is set dynamically from the image's natural dimensions.
    // maxHeight on the wrapper caps tall images; the image scales down via contain.
  },
  hoverOverlay: {
    position: "absolute",
    bottom: spacing.xs,
    right: spacing.xs,
    backgroundColor: "rgba(0,0,0,0.55)",
    borderRadius: radii.md,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xxs,
  },
  hoverText: {
    color: colors.white,
  },
  captionText: {
    color: colors.grey[500],
    paddingHorizontal: spacing.xxs,
    marginTop: spacing.xxs,
  },
  shimmerContainer: {
    width: "100%",
    maxWidth: 480,
    height: 200,
    borderRadius: radii.xl,
    overflow: "hidden",
    backgroundColor: colors.grey[100],
    justifyContent: "center",
    alignItems: "center",
  },
  shimmerText: {
    color: colors.grey[400],
  },
  errorText: {
    color: colors.error[500],
  },
  // Modal
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(15, 23, 42, 0.92)",
    justifyContent: "center",
    alignItems: "center",
    padding: spacing.xl,
  },
  modalCloseButton: {
    position: "absolute",
    top: spacing.md,
    right: spacing.md,
    zIndex: 10,
    width: 40,
    height: 40,
    borderRadius: radii.full,
    backgroundColor: "rgba(255,255,255,0.15)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalCloseText: {
    color: colors.white,
  },
  modalImageContainer: {
    width: "100%",
    maxWidth: 900,
    maxHeight: "80%",
    alignItems: "center",
  },
  modalImage: {
    width: "100%",
    // aspectRatio set dynamically — image takes its natural shape.
  },
  modalCaption: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  modalCaptionText: {
    color: colors.grey[400],
    textAlign: "center",
  },
});
